import moment from 'moment';
import { Admin } from 'stores/interfaces';
import forge from 'node-forge';

export function test() {
	return null;
}

export interface NumberOption {
	value: number;
	label: string;
}

export const ordinalSuffix = (num: number) => {
	if (num >= 11 && num <= 13) return "th"; // special case

	switch (num % 10) {
		case 1:
			return "st";
		case 2:
			return "nd";
		case 3:
			return "rd";
		default:
			return "th";
	}
};

export const getDateListsWithSuffix = (
	start: number,
	total: number,
	extraSuffix: string | null = null
): NumberOption[] => {
	const resps: NumberOption[] = [];

	for (let i = start; i <= total; i++) {
		// determine ordinal suffix
		let ordinal = "th";
		if (i % 100 < 11 || i % 100 > 13) {
			if (i % 10 === 1) ordinal = "st";
			else if (i % 10 === 2) ordinal = "nd";
			else if (i % 10 === 3) ordinal = "rd";
		}

		// build the label
		const label = extraSuffix ? `${i}${ordinal} ${extraSuffix}` : `${i}${ordinal}`;

		resps.push({
			value: i,
			label,
		});
	}

	return resps;
};

export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

export const hasNotch = () => {
	/**
	 * For storybook test
	 */
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (
		(iPhone && aspect.toFixed(3) === '0.462') ||
		(storybook && aspectFrame.toFixed(3) === '0.462')
	);
};

export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

export const priceFormat = (amount: number, currency: string) => {
	let locale: string;

	// Map currency codes to locales
	switch (currency.toUpperCase()) {
		case 'USD':
			locale = 'en-US';
			break;
		case 'NGN':
			locale = 'en-NG';
			break;
		case 'EUR':
			locale = 'de-DE'; // You could use another locale appropriate for Euro formatting
			break;
		case 'GBP':
			locale = 'en-GB';
			break;
		// Add more cases as needed
		default:
			locale = 'en-US'; // Fallback locale
	}

	return amount.toLocaleString(locale, {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
		useGrouping: true,
	});
};

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const percent = (value1: number, value2: number) =>
	Number(((value1 / value2 - 1) * 100).toFixed(2));

export const getFirstLetter = (text: string, letterCount = 2): string =>
	// @ts-ignore
	text
		.toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

export function getEnumValues<T extends string>(enumObj: Record<string, T>): T[] {
	return Object.values(enumObj);
}

export function getEnumOptions<T extends string>(
	enumObj: Record<string, T>,
): { label: string; value: T }[] {
	return Object.keys(enumObj).map((key) => ({
		label: key,
		value: enumObj[key],
	}));
}

export function checkPermission(checking: string, user?: Admin | null): boolean {
	if (!checking) return true;
	if (!user || !user.roles || user.roles.length === 0) return false;

	const role = user.roles[0];
	if (role.name === 'super admin') return true;

	return role.permissions.some((permission) => permission.name === checking);
}

export const capitalizeFirstLetter = (string: string) => {
	if (string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	} else {
		return '';
	}
};

export function ordinalSuffixOf(i: number) {
	const j = i % 10;
	const k = i % 100;
	if (j === 1 && k !== 11) {
		return `${i}st`;
	}
	if (j === 2 && k !== 12) {
		return `${i}nd`;
	}
	if (j === 3 && k !== 13) {
		return `${i}rd`;
	}
	return `${i}th`;
}

export const datFormat = (date: string) => {
	return moment(date).fromNow();
};

export const datFormat2 = (date: string) => {
	return moment(date).format('Do MMMM, YYYY');
};

export const datFormat3 = (date: string) => {
	return moment(date).format('Do MMMM, YYYY hh:mma');
};

export const datFormat4 = (date: string) => {
	return moment(date).format('hh:mm A');
};

export const openPdfForPrint = (base64Data: string) => {
	const base64Prefix = 'data:application/pdf;base64,';
	if (base64Data.startsWith(base64Prefix)) {
		base64Data = base64Data.slice(base64Prefix.length);
	}

	// Decode the Base64 string
	const byteCharacters = atob(base64Data);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);

	// Create a Blob with the appropriate MIME type
	const blob = new Blob([byteArray], { type: 'application/pdf' });
	const blobUrl = URL.createObjectURL(blob);

	// Open the PDF in a new window
	const newWindow = window.open(blobUrl);

	// When the new window has loaded, trigger the print dialog.
	if (newWindow) {
		newWindow.onload = () => {
			newWindow.focus();
			newWindow.print();
		};
	} else {
		console.error('Failed to open new window. Please check your popup blocker settings.');
	}
};

export function getFileExtensionFromBase64(base64String: string): string | null {
	// Regular expression to match the data URL scheme
	const match = base64String.match(/^data:(.*);base64,/);
	if (match && match[1]) {
		// Extract the media type (e.g., image/jpeg, image/png)
		const mediaType = match[1];
		// Split the media type by '/' and return the second part as the file extension
		const extension = mediaType.split('/')[1];
		return extension;
	}
	return null;
}

export const convertFileToBase64 = (file: any) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onloadend = () => {
			resolve(reader.result); // Base64 string
		};

		reader.onerror = (error) => {
			reject(error); // Handle error
		};

		reader.readAsDataURL(file); // Convert file to Base64
	});
};

export function isValidURL(url: string): boolean {
	try {
		new URL(url);
		return true;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (err) {
		return false;
	}
}

/**
 * Map raw stage values to permission strings.
 */
const permissionMap: Record<string, string> = {
	'1': 'loan approval',
	'2': 'create offer letter',
	'3': 'loan disbursement',
	'4': 'update repayments',
};

/**
 * Given an Item, return its permission string (or empty string).
 */
export function getPermission(item?: any): string {
	if (!item?.stage) {
		return '';
	}
	return permissionMap[item.stage.stage] ?? '';
}

export const timeFromMinutes = (startTime: string, endTime: string) => {
	const now = moment(startTime);
	const end = moment(endTime);
	const duration = moment.duration(now.diff(end));
	return duration.asMinutes();
};

export interface RepaymentMonth {
	month: Date; // the due date month
	due: number; // expected amount for that month
	repayments: Array<{
		date: Date; // actual payment date (or due date if missing)
		amount: number; // actual payment amount
	}>;
}

export const encryptRequest = (value: any) => {
	// Generate a random AES key (256-bit for AES-GCM)
	const aesKey = forge.random.getBytesSync(32);

	// Generate a random nonce for AES-GCM
	const nonce = forge.random.getBytesSync(12); // 96-bit nonce for AES-GCM

	// Encrypt the value using AES-GCM
	const cipher = forge.cipher.createCipher('AES-GCM', aesKey);
	cipher.start({ iv: nonce }); // 'iv' is synonymous with 'nonce' in forge
	cipher.update(forge.util.createBuffer(value, 'utf8'));
	cipher.finish();
	const encrypted = cipher.output.getBytes();
	const tag = cipher.mode.tag.getBytes(); // Authentication tag

	// Now, encrypt the AES key using RSA-OAEP
	const publicKeyPem = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsEbCqsKBelt+WyCXEKhJ
/hcr2CZ2DbXH6b7aly7zQkoRX79IOfJ3AZne7fMzEXOQGMHSWUmbo8rpBcyZ+T6M
ZcXe1FJPXRw+76lctB3ulJ2C/XwhpqyjTRm7V6jQpkc8nuPbnBJiw11skwa5+pJ0
GHedalXMxdWQziB6ZMm/IoxVyCoq134BCQfuowJeF8Hqe3ENZSbwd6STGa/KvdGf
C0aYpNA1Upawxi4++QdU7C2sgT2g0yKpdsTCJkpjGC4KzaORdIzUePfvRe4z/Muj
c6eqtviyR0aUoksojI0L4DAAH9dW2dToww/32KdRakYgG2EE0u3aUKqYJKyOKqjB
rwIDAQAB
-----END PUBLIC KEY-----`;

	const publicKey = forge.pki.publicKeyFromPem(publicKeyPem);
	const encryptedAESKey = publicKey.encrypt(aesKey, 'RSA-OAEP', {
		md: forge.md.sha256.create(),
		mgf1: {
			md: forge.md.sha1.create(),
		},
	});

	// Base64-encode the encrypted AES key, nonce, and tag for transmission
	const encodedAESKey = forge.util.encode64(encryptedAESKey);
	const encodedNonce = forge.util.encode64(nonce); // Renamed to nonce
	const encodedEncryptedData = forge.util.encode64(encrypted);
	const encodedTag = forge.util.encode64(tag);

	// Send the encrypted AES key, nonce, tag, and encrypted data
	return {
		aesKey: encodedAESKey,
		nonce: encodedNonce, // Using nonce instead of IV
		encrypted_data: encodedEncryptedData,
		tag: encodedTag,
	};
};
